import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  watchGameGenerationAction,
  stopWatchingGameGenerationAction,
  selectGeneration,
} from '../../createGame/createGameSlice';
import ErrorState from './components/ErrorState';
import StepIndicator from './components/StepIndicator';
import ProgressBar from './components/ProgressBar';
import CategoryProgress from './components/CategoryProgress';
import FinaleCard from './components/FinaleCard';
import TriviaFacts from './components/TriviaFacts';
import BenefitsPanel from './components/BenefitsPanel';
import CancelDialog from './components/CancelDialog';
import DebugPanel from './components/DebugPanel';
import useElapsedTime from './hooks/useElapsedTime';
import useAdaptiveFontSize from './hooks/useAdaptiveFontSize';
import { BrandLockup } from "components";
import logging from 'utils/logging';

const log = logging.getLogger('GameGenerationProgress');

const Root = styled(Box)({
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '48px 24px',
  '&::before': {
    content: '""',
    position: 'fixed',
    inset: 0,
    background: 'linear-gradient(135deg, #faf2d2 0%, #eeeeee 50%, #e1f5f3 100%)',
    zIndex: -1,
  },
});

const Card = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 760,
  background: '#fff',
  borderRadius: 20,
  padding: '56px 56px 40px',
  boxShadow: '0 16px 36px rgba(46,196,182,0.25)',
  border: '2px solid rgba(46,196,182,0.35)',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: '32px 22px 28px',
    borderRadius: 16,
  },
}));

const AccentStrip = styled(Box)({
  position: 'absolute',
  top: 0, left: 0, right: 0,
  height: 6,
  background: 'linear-gradient(90deg, #ffe066 0%, #2ec4b6 100%)',
});

const SectionLabel = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.8rem',
  letterSpacing: '0.32em',
  color: '#2ec4b6',
  fontWeight: 700,
  textTransform: 'uppercase',
  textAlign: 'center',
});

const GameTitle = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  color: '#222',
  textAlign: 'center',
  lineHeight: 1.1,
  marginTop: 6,
  marginBottom: 8,
  wordBreak: 'break-word',
});

const TitleDivider = styled(Box)({
  borderBottom: '2px dashed rgba(0,0,0,0.12)',
  margin: '28px 0',
});

const CancelRow = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 28,
});

const CancelButton = styled(Button)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#888',
  fontSize: '0.85rem',
  borderColor: 'rgba(0,0,0,0.15)',
  '&:hover': {
    borderColor: '#2ec4b6',
    color: '#1a7a72',
    background: 'rgba(46,196,182,0.06)',
  },
});

const ContinueButton = styled(Button)({
  display: 'block',
  width: '100%',
  maxWidth: 360,
  margin: '0 auto',
  padding: '16px 24px',
  background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
  color: '#fff',
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '1.05rem',
  letterSpacing: '0.18em',
  fontWeight: 800,
  textTransform: 'uppercase',
  borderRadius: 14,
  boxShadow: '0 8px 20px rgba(46,196,182,0.35)',
  '&:hover': {
    background: 'linear-gradient(135deg, #2ec4b6 0%, #1a7a72 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 28px rgba(46,196,182,0.45)',
  },
});

const GameGenerationProgress = ({ gameId, gameName, onComplete }) => {
  const [cancelOpen, setCancelOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const generation = useSelector(selectGeneration);

  const progress = generation?.overallProgress || 0;
  const currentPhase = generation?.currentPhase || generation?.status || 'initializing';
  const categoryCount = generation?.categoryCount || 0;
  const roundCount = generation?.roundCount || 1;
  const categoryNames = generation?.categoryNames || [];
  const displayName = gameName || generation?.name || '';

  const { formattedElapsed, formattedRemaining, elapsedSeconds } = useElapsedTime(
    generation?.startedAt,
    progress,
    generation?.estimatedTimeRemaining
  );

  const titleFontSize = useAdaptiveFontSize(displayName, { baseRem: 2.6, minRem: 1.6 });

  useEffect(() => {
    if (gameId) dispatch(watchGameGenerationAction(gameId));
    return () => {
      if (gameId) dispatch(stopWatchingGameGenerationAction(gameId));
    };
  }, [dispatch, gameId]);

  // Track previous status so we can distinguish a real-time transition (incomplete → complete,
  // auto-navigate to /done) from arriving at /progress with already-complete state
  // (back-nav from /done — show last state with a Continue button instead).
  const prevStatusRef = useRef(null);
  useEffect(() => {
    const status = generation?.status;
    if (
      gameId &&
      prevStatusRef.current &&
      prevStatusRef.current !== 'complete' &&
      status === 'complete'
    ) {
      log.info('Game generation completed — navigating to done screen');
      navigate(`/manage/create/ai/${gameId}/done`);
    }
    prevStatusRef.current = status;
  }, [generation?.status, gameId, navigate]);

  const isFailed = generation?.status === 'failed';
  const isComplete = generation?.status === 'complete';

  const handleCancelClick = () => {
    setCancelOpen(true);
  };

  return (
    <Root>
      <Card>
        <AccentStrip sx={isFailed ? { background: 'linear-gradient(90deg, #ff9c4a 0%, #d04a3a 100%)' } : undefined} />
        <BrandLockup size="md" variant="light" sx={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }} />

        {isFailed ? (
          <ErrorState
            gameId={gameId}
            gameName={displayName}
            lastCompletedPhase={generation.lastCompletedPhase}
            errorMessage={generation.errorMessage}
            failedAt={generation.failedAt}
            onCancel={() => setCancelOpen(true)}
          />
        ) : (
          <>
            <SectionLabel>Building Your Game</SectionLabel>
            <GameTitle sx={{ fontSize: titleFontSize }}>{displayName}</GameTitle>

            <TitleDivider />

            <StepIndicator currentPhase={currentPhase} categoryCount={categoryCount} />
            <ProgressBar
              progress={progress}
              formattedElapsed={formattedElapsed}
              formattedRemaining={formattedRemaining}
            />
            <CategoryProgress
              categoryCount={categoryCount}
              roundCount={roundCount}
              categoryProgress={generation?.categories}
              categoryNames={categoryNames}
              currentPhase={currentPhase}
            />
            <FinaleCard currentPhase={currentPhase} />
            <TriviaFacts />
            <BenefitsPanel />
            <DebugPanel generation={generation} />

            <CancelRow>
              {isComplete ? (
                <ContinueButton onClick={() => navigate(`/manage/create/ai/${gameId}/done`)}>
                  Continue
                </ContinueButton>
              ) : (
                <CancelButton variant="outlined" onClick={handleCancelClick}>Cancel</CancelButton>
              )}
            </CancelRow>
          </>
        )}

        <CancelDialog
          open={cancelOpen}
          onClose={() => setCancelOpen(false)}
          gameId={gameId}
          phaseAtCancel={currentPhase}
          elapsedSeconds={elapsedSeconds}
        />
      </Card>
    </Root>
  );
};

export default GameGenerationProgress;
